import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { formatPrice } from "@/lib/format";
import { useLanguage } from "@/context/LanguageContext";
import ImageUpload from "@/components/admin/ImageUpload";
import api, { apiError } from "@/lib/api";

const EMPTY = { name_es: "", name_en: "", description_es: "", description_en: "", price: "", image: "" };

export default function AdminAccessories() {
  const { lang } = useLanguage();
  const es = lang === "es";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/accessories").then((r) => setItems(r.data.accessories)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ name_es: a.name_es || "", name_en: a.name_en || "", description_es: a.description_es || "", description_en: a.description_en || "", price: a.price ?? "", image: a.image || "" });
    setDialogOpen(true);
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name_es: form.name_es, name_en: form.name_en,
      description_es: form.description_es, description_en: form.description_en,
      price: parseFloat(form.price) || 0, image: form.image, status: "active",
    };
    try {
      if (editing) await api.put(`/admin/accessories/${editing.id}`, payload);
      else await api.post("/admin/accessories", payload);
      toast.success(editing ? (es ? "Accesorio actualizado" : "Accessory updated") : (es ? "Accesorio creado" : "Accessory created"));
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    await api.delete(`/admin/accessories/${deleteId}`);
    toast.success(es ? "Accesorio eliminado" : "Accessory deleted");
    setDeleteId(null); load();
  };

  const toggleAvailable = async (a) => {
    const status = a.status === "active" ? "draft" : "active";
    setItems((prev) => prev.map((x) => (x.id === a.id ? { ...x, status } : x)));
    try {
      await api.put(`/admin/accessories/${a.id}`, {
        name_es: a.name_es, name_en: a.name_en,
        description_es: a.description_es, description_en: a.description_en,
        price: a.price, image: a.image, status,
      });
      toast.success(status === "active" ? (es ? "Visible en la tienda" : "Visible in store") : (es ? "Oculto de la tienda" : "Hidden from store"));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      load();
    }
  };

  return (
    <div data-testid="admin-inventory-accessories">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-4xl font-light">{es ? "Inventario · Accesorios" : "Inventory · Accessories"}</h1>
          <p className="text-muted-foreground text-sm mt-1">{items.length} {es ? "accesorios" : "accessories"}</p>
        </div>
        <Button className="rounded-none" onClick={openNew} data-testid="add-accessory-btn"><Plus className="h-4 w-4 mr-1" /> {es ? "Añadir accesorio" : "Add accessory"}</Button>
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        {loading ? (
          <div className="py-24 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="p-4 font-medium overline">{es ? "Accesorio" : "Accessory"}</th>
                <th className="p-4 font-medium overline">{es ? "Precio" : "Price"}</th>
                <th className="p-4 font-medium overline text-center hidden sm:table-cell">{es ? "Visible" : "Visible"}</th>
                <th className="p-4 font-medium overline text-right">{es ? "Acciones" : "Actions"}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid={`accessory-row-${a.id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-14 bg-secondary overflow-hidden shrink-0"><img src={a.image} alt={a.name_es} className="h-full w-full object-cover" /></div>
                      <div><p className="font-medium">{a[`name_${lang}`] || a.name_es}</p><p className="text-xs text-muted-foreground line-clamp-1 max-w-xs">{a[`description_${lang}`]}</p></div>
                    </div>
                  </td>
                  <td className="p-4">{formatPrice(a.price)}</td>
                  <td className="p-4 text-center hidden sm:table-cell">
                    <Switch checked={a.status !== "draft"} onCheckedChange={() => toggleAvailable(a)} data-testid={`toggle-accessory-${a.id}`} />
                  </td>
                  <td className="p-4">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(a)} data-testid={`edit-accessory-${a.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => setDeleteId(a.id)} className="hover:text-destructive" data-testid={`delete-accessory-${a.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none max-w-xl max-h-[90vh] overflow-y-auto" data-testid="accessory-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editing ? (es ? "Editar accesorio" : "Edit accessory") : (es ? "Nuevo accesorio" : "New accessory")}</DialogTitle>
            <DialogDescription className="sr-only">Accessory form</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="overline text-muted-foreground">Nombre (ES)</Label><Input required value={form.name_es} onChange={set("name_es")} className="rounded-none mt-1.5" data-testid="af-name-es" /></div>
              <div><Label className="overline text-muted-foreground">Name (EN)</Label><Input required value={form.name_en} onChange={set("name_en")} className="rounded-none mt-1.5" data-testid="af-name-en" /></div>
            </div>
            <div><Label className="overline text-muted-foreground">Características (ES)</Label><Textarea rows={2} value={form.description_es} onChange={set("description_es")} className="rounded-none mt-1.5 resize-none" data-testid="af-desc-es" /></div>
            <div><Label className="overline text-muted-foreground">Characteristics (EN)</Label><Textarea rows={2} value={form.description_en} onChange={set("description_en")} className="rounded-none mt-1.5 resize-none" data-testid="af-desc-en" /></div>
            <div><Label className="overline text-muted-foreground">{es ? "Precio ($)" : "Price ($)"}</Label><Input type="number" step="0.01" required value={form.price} onChange={set("price")} className="rounded-none mt-1.5 max-w-[200px]" data-testid="af-price" /></div>
            <ImageUpload value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} label={es ? "Imagen" : "Image"} testid="af-image" />
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="rounded-none flex-1" disabled={saving} data-testid="save-accessory-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? (es ? "Guardar cambios" : "Save changes") : (es ? "Crear accesorio" : "Create accessory"))}</Button>
              <Button type="button" variant="outline" className="rounded-none" onClick={() => setDialogOpen(false)}>{es ? "Cancelar" : "Cancel"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader><AlertDialogTitle>{es ? "¿Eliminar este accesorio?" : "Delete this accessory?"}</AlertDialogTitle><AlertDialogDescription>{es ? "Esta acción no se puede deshacer." : "This action cannot be undone."}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">{es ? "Cancelar" : "Cancel"}</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="rounded-none bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-accessory">{es ? "Eliminar" : "Delete"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
